import React, { useState, useEffect, useMemo } from 'react';
import {
  Flex,
  HStack,
  Box,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Button,
  Select,
  Avatar,
  Tooltip,
  Icon,
} from '@chakra-ui/react';
import { FiMenu, FiSearch, FiX } from 'react-icons/fi';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { useSelector } from 'react-redux';
import { HeaderNotificationBell } from '../../header/HeaderNotificationBell';
import { formatedLastName } from '../../../../utils/FormatedLastName';
import { useUnreadEvents } from '../../../../context/UnreadEventsContext';
import { getEventPriority } from '../../../../utils/clinicalEvents';
import { useGetAllCenterQuery } from '../../../../redux/api/centerApi';
import { useGetAllUsersQuery } from '../../../../redux/api/userApi';

export const MobileNav = ({ onOpen, ...rest }: any) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { user } = useSelector((state: any) => state.auth || {});
  const { allEvents } = useUnreadEvents();

  // Detección dinámica de ruta activa
  const pathname = location.pathname;
  const isHome = pathname === '/home' || pathname === '/';
  const isCenters = pathname.startsWith('/centers');
  const isUsers = pathname.startsWith('/users');
  const isEvents = pathname.startsWith('/events');
  const isDevices = pathname.startsWith('/devices');
  const isPatients = pathname === '/patients' || pathname === '/patients/';
  const isCoverage = pathname.startsWith('/coverage');

  const isUserSuperAdmin = Boolean(
    user?.isAdmin === true ||
    user?.isAdmin === 'true' ||
    user?.isAdmin === 1 ||
    user?.isAdmin === '1' ||
    user?.role === 'superadmin' ||
    (user?.isAdmin && user?.role === 'administrador')
  );

  const shouldShowSearch = useMemo(() => {
    if (isCoverage) return false;
    if (isHome && isUserSuperAdmin) return false;
    return true;
  }, [isCoverage, isHome, isUserSuperAdmin]);

  const urlQuery = searchParams.get('q') || '';
  const activeFilter = searchParams.get('filter') || 'all';

  // Filtros específicos para Dispositivos (/devices)
  const deviceTypeFilter = searchParams.get('type') || 'all';
  const deviceAssignmentFilter = searchParams.get('assignment') || 'all';
  const deviceCenterFilter = searchParams.get('center') || 'all';

  const [searchText, setSearchText] = useState(urlQuery);

  // Sincroniza estado local con searchParams
  useEffect(() => {
    setSearchText(urlQuery);
  }, [urlQuery]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchText(val);

    const next = new URLSearchParams(searchParams);
    if (val) next.set('q', val);
    else next.delete('q');
    setSearchParams(next, { replace: true });
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      handleClearSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchText('');
    const next = new URLSearchParams(searchParams);
    next.delete('q');
    setSearchParams(next, { replace: true });
  };

  const handleFilterSelect = (tab: string) => {
    const next = new URLSearchParams(searchParams);
    if (tab && tab !== 'all') {
      next.set('filter', tab);
    } else {
      next.delete('filter');
    }
    next.delete('page');
    setSearchParams(next, { replace: true });
  };

  const activeDevice = searchParams.get('tab') || 'all';
  const activeType = searchParams.get('type') || '';

  const handleDeviceSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    const next = new URLSearchParams(searchParams);
    if (val && val !== 'all') {
      next.set('tab', val);
      next.set('device', val);
    } else {
      next.delete('tab');
      next.delete('device');
    }
    next.delete('page');
    setSearchParams(next, { replace: true });
  };

  const handleTypeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    const next = new URLSearchParams(searchParams);
    if (val) {
      next.set('type', val);
    } else {
      next.delete('type');
    }
    next.delete('page');
    setSearchParams(next, { replace: true });
  };

  const handleDeviceTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    const next = new URLSearchParams(searchParams);
    if (val && val !== 'all') {
      next.set('type', val);
    } else {
      next.delete('type');
    }
    next.delete('page');
    setSearchParams(next, { replace: true });
  };

  const handleDeviceAssignmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    const next = new URLSearchParams(searchParams);
    if (val && val !== 'all') {
      next.set('assignment', val);
    } else {
      next.delete('assignment');
    }
    next.delete('page');
    setSearchParams(next, { replace: true });
  };

  const handleDeviceCenterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    const next = new URLSearchParams(searchParams);
    if (val && val !== 'all') {
      next.set('center', val);
    } else {
      next.delete('center');
    }
    next.delete('page');
    setSearchParams(next, { replace: true });
  };

  // Placeholder dinámico según la vista
  const searchPlaceholder = useMemo(() => {
    if (isCenters) return t('Buscar por centro, ciudad, CIF o teléfono...') || 'Buscar por centro, ciudad, CIF o teléfono…';
    if (isUsers) return t('Buscar por usuario, email o rol...') || 'Buscar por usuario, email o rol…';
    if (isEvents) return t('Search by patient, center, IMEI...') || 'Buscar por paciente, centro, IMEI...';
    if (isPatients) return t('Search by patient, surname, DNI...') || 'Buscar por paciente, apellidos, DNI...';
    if (isDevices) return t('Buscar por IMEI, serial, modelo, paciente o centro...') || 'Buscar por IMEI, serial, modelo, paciente o centro…';
    return t('Buscar por paciente, DNI o telemetría...') || 'Buscar por paciente, DNI o telemetría…';
  }, [isCenters, isUsers, isEvents, isPatients, isDevices, t]);

  // Datos para el cluster de triaje (Home)
  const unreadList = Array.isArray(allEvents) ? allEvents.filter((e: any) => !e.read) : [];
  const criticalCount = unreadList.filter((e: any) => {
    const type = e.name || e.type || '';
    return getEventPriority(type) <= 4 || ['SOS', 'FALLDOWN', 'SHOCK', 'GEO_FENCE', 'OXYGEN', 'TEMPERATURE'].includes(type);
  }).length;
  const warnCount = Math.max(0, unreadList.length - criticalCount);

  // Datos para el cluster de Centros (Centros Asistenciales) y Dispositivos
  const { data: allCenters = [] } = useGetAllCenterQuery(undefined, { skip: !isCenters && !isDevices });
  const totalCenters = Array.isArray(allCenters) ? allCenters.length : 0;
  const activeCenters = Array.isArray(allCenters) ? allCenters.filter((c: any) => !c?.deletedAt).length : 0;

  // Datos para el cluster de Gestión de Usuarios y Roles
  const { data: usersData = [] } = useGetAllUsersQuery(undefined, { skip: !isUsers });
  const allUsers = useMemo(() => (Array.isArray(usersData) ? usersData : (usersData?.users || [])), [usersData]);
  const isUserCorp = user?.role === 'corporacion';

  const accessibleUsersList = useMemo(() => {
    if (!Array.isArray(allUsers)) return [];
    if (isUserSuperAdmin) {
      return allUsers.filter((u: any) => u?.role === 'corporacion');
    }
    if (isUserCorp) {
      return allUsers.filter((u: any) => u?.role !== 'corporacion' && !u?.isAdmin);
    }
    return allUsers.filter((u: any) => u?.role !== 'corporacion' && u?.role !== 'administrador' && !u?.isAdmin);
  }, [allUsers, isUserSuperAdmin, isUserCorp]);

  const userStats = useMemo(() => {
    const total = accessibleUsersList.length;
    const medicos = accessibleUsersList.filter((u: any) => u?.role === 'medico').length;
    const enfermeras = accessibleUsersList.filter((u: any) => u?.role === 'enfermera').length;
    const cuidadores = accessibleUsersList.filter((u: any) => ['cuidadora', 'auxiliar', 'fisio'].includes(u?.role)).length;
    const admin = accessibleUsersList.filter((u: any) => ['administrador', 'corporacion'].includes(u?.role)).length;
    return { total, medicos, enfermeras, cuidadores, admin };
  }, [accessibleUsersList]);

  const userFullName = `${user?.name || ''} ${formatedLastName(user?.lastname) || ''}`.trim() || t('Usuario');
  const displayRole = user?.isAdmin
    ? 'SuperAdmin'
    : user?.role
    ? user.role.charAt(0).toUpperCase() + user.role.slice(1)
    : 'Supervisión Médica';

  return (
    <Flex
      as="header"
      position="sticky"
      top="0"
      zIndex="docked"
      ml={{ base: 0, xl: '256px' }}
      px={{ base: 3, md: 6 }}
      height="64px"
      alignItems="center"
      bg="rgba(255, 255, 255, 0.85)"
      backdropFilter="blur(16px)"
      borderBottom="1px solid"
      borderBottomColor="gray.200"
      justifyContent="space-between"
      boxShadow="0 1px 4px rgba(0, 0, 0, 0.03)"
      {...rest}
    >
      {/* 1. Left: Hamburger (mobile) + Dynamic Search Input */}
      <HStack
        spacing={3}
        align="center"
        flex={shouldShowSearch ? '1' : { base: '1', md: 'initial' }}
        maxW={shouldShowSearch ? { base: '100%', md: '380px', lg: '440px' } : undefined}
      >
        <IconButton
          type="button"
          display={{ base: 'flex', xl: 'none' }}
          onClick={onOpen}
          variant="ghost"
          color="gray.700"
          aria-label="open menu"
          icon={<Icon as={FiMenu} boxSize={5} />}
          size="sm"
          borderRadius="lg"
          _hover={{ bg: "gray.100" }}
          flexShrink={0}
        />

        {shouldShowSearch && (
          <InputGroup size="sm" w="full">
            <InputLeftElement pointerEvents="none" h="100%">
              <FiSearch color="var(--chakra-colors-gray-400)" />
            </InputLeftElement>
            <Input
              value={searchText}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              placeholder={searchPlaceholder}
              borderRadius="xl"
              bg="gray.100"
              borderColor="transparent"
              _hover={{ bg: 'white', borderColor: 'gray.300' }}
              _focus={{
                bg: 'white',
                borderColor: 'teal.400',
                boxShadow: '0 0 0 1px var(--chakra-colors-teal-400)',
              }}
              fontSize="13px"
              h="38px"
              pl="36px"
              pr="32px"
            />
            {searchText && (
              <InputRightElement h="100%">
                <IconButton
                  type="button"
                  aria-label={t('Clear search') || 'Limpiar búsqueda'}
                  icon={<FiX />}
                  size="xs"
                  variant="ghost"
                  color="gray.400"
                  _hover={{ color: 'gray.600' }}
                  onClick={handleClearSearch}
                />
              </InputRightElement>
            )}
          </InputGroup>
        )}
      </HStack>

      {/* 2. Center: Dynamic Filter Pill Cluster based on active view */}
      {/* 2.1 Cluster de Triaje (Home / Pacientes) */}
      {isHome && !isUserSuperAdmin && (
        <HStack
          spacing={1.5}
          p="3px"
          bg="gray.100"
          borderRadius="xl"
          display={{ base: 'none', lg: 'flex' }}
          mx={4}
        >
          <Button
            size="xs"
            h="28px"
            px={3}
            borderRadius="lg"
            variant="ghost"
            bg={activeFilter === 'critical' ? 'red.500' : 'transparent'}
            color={activeFilter === 'critical' ? 'white' : criticalCount > 0 ? 'red.600' : 'gray.600'}
            _hover={{ bg: activeFilter === 'critical' ? 'red.600' : 'red.50' }}
            onClick={() => handleFilterSelect('critical')}
            fontSize="11px"
            fontWeight="bold"
            leftIcon={
              criticalCount > 0 ? (
                <Box
                  as="span"
                  w="6px"
                  h="6px"
                  borderRadius="full"
                  bg={activeFilter === 'critical' ? 'white' : 'red.500'}
                  animation="pulse 2s infinite"
                />
              ) : undefined
            }
          >
            {t('Crítico') || 'Crítico'} ({criticalCount})
          </Button>

          <Button
            size="xs"
            h="28px"
            px={3}
            borderRadius="lg"
            variant="ghost"
            bg={activeFilter === 'warn' ? 'orange.400' : 'transparent'}
            color={activeFilter === 'warn' ? 'white' : 'gray.600'}
            _hover={{ bg: activeFilter === 'warn' ? 'orange.500' : 'orange.50' }}
            onClick={() => handleFilterSelect('warn')}
            fontSize="11px"
            fontWeight="bold"
          >
            {t('Atención') || 'Atención'} ({warnCount})
          </Button>

          <Button
            size="xs"
            h="28px"
            px={3}
            borderRadius="lg"
            variant="ghost"
            bg={activeFilter === 'stable' ? 'teal.500' : 'transparent'}
            color={activeFilter === 'stable' ? 'white' : 'gray.600'}
            _hover={{ bg: activeFilter === 'stable' ? 'teal.600' : 'teal.50' }}
            onClick={() => handleFilterSelect('stable')}
            fontSize="11px"
            fontWeight="bold"
          >
            {t('Estable') || 'Estable'}
          </Button>

          <Button
            size="xs"
            h="28px"
            px={3}
            borderRadius="lg"
            variant="ghost"
            bg={activeFilter === 'offline' ? 'gray.600' : 'transparent'}
            color={activeFilter === 'offline' ? 'white' : 'gray.600'}
            _hover={{ bg: activeFilter === 'offline' ? 'gray.700' : 'gray.200' }}
            onClick={() => handleFilterSelect('offline')}
            fontSize="11px"
            fontWeight="bold"
          >
            {t('Offline') || 'Offline'}
          </Button>

          <Button
            size="xs"
            h="28px"
            px={3}
            borderRadius="lg"
            variant="ghost"
            bg={activeFilter === 'all' ? 'gray.700' : 'transparent'}
            color={activeFilter === 'all' ? 'white' : 'gray.600'}
            _hover={{ bg: activeFilter === 'all' ? 'gray.800' : 'gray.200' }}
            onClick={() => handleFilterSelect('all')}
            fontSize="11px"
            fontWeight="bold"
          >
            {t('Todos') || 'Todos'}
          </Button>
        </HStack>
      )}

      {/* 2.2 Cluster de Centros Asistenciales (Stitch Centros) */}
      {isCenters && (
        <HStack
          spacing={1.5}
          p="3px"
          bg="gray.100"
          borderRadius="xl"
          display={{ base: 'none', lg: 'flex' }}
          mx={4}
        >
          <Button
            size="xs"
            h="28px"
            px={3}
            borderRadius="lg"
            variant="ghost"
            bg={activeFilter === 'all' || !activeFilter ? 'teal.500' : 'transparent'}
            color={activeFilter === 'all' || !activeFilter ? 'white' : 'gray.600'}
            _hover={{ bg: activeFilter === 'all' || !activeFilter ? 'teal.600' : 'gray.200' }}
            onClick={() => handleFilterSelect('all')}
            fontSize="11px"
            fontWeight="bold"
          >
            {t('Todos') || 'Todos'} ({totalCenters})
          </Button>

          <Button
            size="xs"
            h="28px"
            px={3}
            borderRadius="lg"
            variant="ghost"
            bg={activeFilter === 'operativos' || activeFilter === 'active' ? 'teal.600' : 'transparent'}
            color={activeFilter === 'operativos' || activeFilter === 'active' ? 'white' : 'gray.600'}
            _hover={{ bg: activeFilter === 'operativos' || activeFilter === 'active' ? 'teal.700' : 'teal.50' }}
            onClick={() => handleFilterSelect('operativos')}
            fontSize="11px"
            fontWeight="bold"
            leftIcon={
              <Box
                as="span"
                w="6px"
                h="6px"
                borderRadius="full"
                bg={activeFilter === 'operativos' || activeFilter === 'active' ? 'white' : 'teal.500'}
              />
            }
          >
            {t('Operativos') || 'Operativos'} ({activeCenters})
          </Button>

          <Button
            size="xs"
            h="28px"
            px={3}
            borderRadius="lg"
            variant="ghost"
            bg={activeFilter === 'sobrecarga' ? 'red.500' : 'transparent'}
            color={activeFilter === 'sobrecarga' ? 'white' : 'gray.600'}
            _hover={{ bg: activeFilter === 'sobrecarga' ? 'red.600' : 'red.50' }}
            onClick={() => handleFilterSelect('sobrecarga')}
            fontSize="11px"
            fontWeight="bold"
          >
            {t('Sobrecarga') || 'Sobrecarga'} (0)
          </Button>

          <Button
            size="xs"
            h="28px"
            px={3}
            borderRadius="lg"
            variant="ghost"
            bg={activeFilter === 'mantenimiento' ? 'gray.700' : 'transparent'}
            color={activeFilter === 'mantenimiento' ? 'white' : 'gray.600'}
            _hover={{ bg: activeFilter === 'mantenimiento' ? 'gray.800' : 'gray.200' }}
            onClick={() => handleFilterSelect('mantenimiento')}
            fontSize="11px"
            fontWeight="bold"
          >
            {t('Mantenimiento') || 'Mantenimiento'} (0)
          </Button>
        </HStack>
      )}

      {/* 2.3 Cluster de Gestión de Usuarios y Roles (Stitch Usuarios - solo para administradores con personal clínico/asistencial) */}
      {isUsers && !isUserSuperAdmin && (
        <HStack
          spacing={1.5}
          p="3px"
          bg="gray.100"
          borderRadius="xl"
          display={{ base: 'none', lg: 'flex' }}
          mx={4}
        >
          <Button
            size="xs"
            h="28px"
            px={3}
            borderRadius="lg"
            variant="ghost"
            bg={activeFilter === 'all' || !activeFilter ? 'teal.500' : 'transparent'}
            color={activeFilter === 'all' || !activeFilter ? 'white' : 'gray.600'}
            _hover={{ bg: activeFilter === 'all' || !activeFilter ? 'teal.600' : 'gray.200' }}
            onClick={() => handleFilterSelect('all')}
            fontSize="11px"
            fontWeight="bold"
          >
            {t('Todos') || 'Todos'} ({userStats.total})
          </Button>

          <Button
            size="xs"
            h="28px"
            px={3}
            borderRadius="lg"
            variant="ghost"
            bg={activeFilter === 'medico' ? 'teal.600' : 'transparent'}
            color={activeFilter === 'medico' ? 'white' : 'gray.600'}
            _hover={{ bg: activeFilter === 'medico' ? 'teal.700' : 'teal.50' }}
            onClick={() => handleFilterSelect('medico')}
            fontSize="11px"
            fontWeight="bold"
          >
            {t('Médicos') || 'Médicos'} ({userStats.medicos})
          </Button>

          <Button
            size="xs"
            h="28px"
            px={3}
            borderRadius="lg"
            variant="ghost"
            bg={activeFilter === 'enfermera' ? 'blue.500' : 'transparent'}
            color={activeFilter === 'enfermera' ? 'white' : 'gray.600'}
            _hover={{ bg: activeFilter === 'enfermera' ? 'blue.600' : 'blue.50' }}
            onClick={() => handleFilterSelect('enfermera')}
            fontSize="11px"
            fontWeight="bold"
          >
            {t('Enfermería') || 'Enfermería'} ({userStats.enfermeras})
          </Button>

          <Button
            size="xs"
            h="28px"
            px={3}
            borderRadius="lg"
            variant="ghost"
            bg={activeFilter === 'cuidadora' || activeFilter === 'cuidadores' ? 'orange.500' : 'transparent'}
            color={activeFilter === 'cuidadora' || activeFilter === 'cuidadores' ? 'white' : 'gray.600'}
            _hover={{ bg: activeFilter === 'cuidadora' || activeFilter === 'cuidadores' ? 'orange.600' : 'orange.50' }}
            onClick={() => handleFilterSelect('cuidadora')}
            fontSize="11px"
            fontWeight="bold"
          >
            {t('Cuidadores') || 'Cuidadores'} ({userStats.cuidadores})
          </Button>

          {isUserCorp && (
            <Button
              size="xs"
              h="28px"
              px={3}
              borderRadius="lg"
              variant="ghost"
              bg={activeFilter === 'administrador' ? 'gray.700' : 'transparent'}
              color={activeFilter === 'administrador' ? 'white' : 'gray.600'}
              _hover={{ bg: activeFilter === 'administrador' ? 'gray.800' : 'gray.200' }}
              onClick={() => handleFilterSelect('administrador')}
              fontSize="11px"
              fontWeight="bold"
            >
              {t('Administración') || 'Administración'} ({userStats.admin})
            </Button>
          )}
        </HStack>
      )}

      {/* 2.4 Cluster de Eventos y Alertas (Stitch Alertas + Dispositivo + Tipo de Alerta) */}
      {isEvents && (
        <HStack
          spacing={2}
          display={{ base: 'none', lg: 'flex' }}
          mx={3}
          align="center"
        >
          {/* Píldoras de Severidad */}
          <HStack
            spacing={1}
            p="3px"
            bg="gray.100"
            borderRadius="xl"
          >
            <Button
              size="xs"
              h="28px"
              px={2.5}
              borderRadius="lg"
              variant="ghost"
              bg={activeFilter === 'all' || !activeFilter ? 'teal.500' : 'transparent'}
              color={activeFilter === 'all' || !activeFilter ? 'white' : 'gray.600'}
              _hover={{ bg: activeFilter === 'all' || !activeFilter ? 'teal.600' : 'gray.200' }}
              onClick={() => handleFilterSelect('all')}
              fontSize="11px"
              fontWeight="bold"
            >
              {t('Todas') || 'Todas'} ({unreadList.length})
            </Button>

            <Button
              size="xs"
              h="28px"
              px={2.5}
              borderRadius="lg"
              variant="ghost"
              bg={activeFilter === 'critical' || activeFilter === 'P0' ? 'red.500' : 'transparent'}
              color={activeFilter === 'critical' || activeFilter === 'P0' ? 'white' : criticalCount > 0 ? 'red.600' : 'gray.600'}
              _hover={{ bg: activeFilter === 'critical' || activeFilter === 'P0' ? 'red.600' : 'red.50' }}
              onClick={() => handleFilterSelect('critical')}
              fontSize="11px"
              fontWeight="bold"
              leftIcon={
                criticalCount > 0 ? (
                  <Box
                    as="span"
                    w="6px"
                    h="6px"
                    borderRadius="full"
                    bg={activeFilter === 'critical' || activeFilter === 'P0' ? 'white' : 'red.500'}
                    animation="pulse 2s infinite"
                  />
                ) : undefined
              }
            >
              {t('Crítico (P0)') || 'Crítico (P0)'} ({criticalCount})
            </Button>

            <Button
              size="xs"
              h="28px"
              px={2.5}
              borderRadius="lg"
              variant="ghost"
              bg={activeFilter === 'warn' || activeFilter === 'P1' ? 'orange.500' : 'transparent'}
              color={activeFilter === 'warn' || activeFilter === 'P1' ? 'white' : 'gray.600'}
              _hover={{ bg: activeFilter === 'warn' || activeFilter === 'P1' ? 'orange.600' : 'orange.50' }}
              onClick={() => handleFilterSelect('warn')}
              fontSize="11px"
              fontWeight="bold"
            >
              {t('Clínico (P1)') || 'Clínico (P1)'} ({warnCount})
            </Button>

            <Button
              size="xs"
              h="28px"
              px={2.5}
              borderRadius="lg"
              variant="ghost"
              bg={activeFilter === 'tech' || activeFilter === 'P2' ? 'blue.500' : 'transparent'}
              color={activeFilter === 'tech' || activeFilter === 'P2' ? 'white' : 'gray.600'}
              _hover={{ bg: activeFilter === 'tech' || activeFilter === 'P2' ? 'blue.600' : 'blue.50' }}
              onClick={() => handleFilterSelect('tech')}
              fontSize="11px"
              fontWeight="bold"
            >
              {t('Técnico (P2)') || 'Técnico (P2)'}
            </Button>
          </HStack>

          {/* Rediseño Filtro 1: Selector de Dispositivo */}
          <Select
            size="xs"
            h="28px"
            w={{ base: '155px', xl: '175px' }}
            borderRadius="lg"
            bg="white"
            borderColor="gray.200"
            fontSize="11px"
            fontWeight="semibold"
            color="gray.700"
            value={activeDevice}
            onChange={handleDeviceSelect}
            aria-label={t('Filter by device') || 'Filtrar por dispositivo'}
            cursor="pointer"
            _hover={{ borderColor: 'teal.400' }}
            _focus={{ borderColor: 'teal.500', boxShadow: 'none' }}
          >
            <option value="all">{t('Dispositivo: Todos') || 'Dispositivo: Todos'}</option>
            <option value="smartwatch">{t('Solo Smartwatch') || 'Solo Smartwatch'}</option>
            <option value="emergency-button">{t('Solo Botones SOS') || 'Solo Botones SOS'}</option>
          </Select>

          {/* Rediseño Filtro 2: Selector de Tipo de Alarma */}
          <Select
            size="xs"
            h="28px"
            w={{ base: '155px', xl: '170px' }}
            borderRadius="lg"
            bg="white"
            borderColor="gray.200"
            fontSize="11px"
            fontWeight="semibold"
            color="gray.700"
            value={activeType}
            onChange={handleTypeSelect}
            aria-label={t('Filter by event type') || 'Filtrar por tipo de evento'}
            cursor="pointer"
            _hover={{ borderColor: 'teal.400' }}
            _focus={{ borderColor: 'teal.500', boxShadow: 'none' }}
          >
            <option value="">{t('Tipo: Todas') || 'Tipo: Todas'}</option>
            <option value="FALLDOWN">{t('Fall detected') || 'Caída'}</option>
            <option value="SOS">{t('Emergency button pressed') || 'SOS Pulsador'}</option>
            <option value="SHOCK">{t('Crash detected') || 'Pulsaciones'}</option>
            <option value="OXYGEN">{t('Oxygen level') || 'Oxígeno (SpO2)'}</option>
            <option value="BLOOD_PRESSURE">{t('Blood pressure') || 'Presión Arterial'}</option>
            <option value="TEMPERATURE">{t('Body temperature') || 'Temperatura'}</option>
            <option value="GEO_FENCE">{t('GpsFence') || 'Geovalla'}</option>
            <option value="LOW_BATTERY">{t('Low battery') || 'Batería Baja'}</option>
            <option value="WATCH_REMOVED">{t('Retired watch') || 'Reloj Retirado'}</option>
            <option value="WATCH_DISCONNECTED">{t('Disconnected watch') || 'Desconectado'}</option>
            <option value="MEDICINE_REMINDER">{t('Medication reminder') || 'Medicación'}</option>
          </Select>
        </HStack>
      )}

      {/* 2.5 Cluster de Pacientes (Stitch Pacientes) */}
      {isPatients && (
        <HStack spacing={2} display={{ base: 'none', lg: 'flex' }} mx={3}>
          <HStack
            spacing={1.5}
            p="3px"
            bg="gray.100"
            borderRadius="xl"
          >
            <Button
              size="xs"
              h="28px"
              px={3}
              borderRadius="lg"
              variant="ghost"
              bg={activeFilter === 'active' || activeFilter === 'all' || !activeFilter ? 'teal.500' : 'transparent'}
              color={activeFilter === 'active' || activeFilter === 'all' || !activeFilter ? 'white' : 'gray.600'}
              _hover={{ bg: activeFilter === 'active' || activeFilter === 'all' || !activeFilter ? 'teal.600' : 'gray.200' }}
              onClick={() => handleFilterSelect('active')}
              fontSize="11px"
              fontWeight="bold"
            >
              {t('Actives') || 'Activos'}
            </Button>

            <Button
              size="xs"
              h="28px"
              px={3}
              borderRadius="lg"
              variant="ghost"
              bg={activeFilter === 'historical' ? 'red.500' : 'transparent'}
              color={activeFilter === 'historical' ? 'white' : 'gray.600'}
              _hover={{ bg: activeFilter === 'historical' ? 'red.600' : 'red.50' }}
              onClick={() => handleFilterSelect('historical')}
              fontSize="11px"
              fontWeight="bold"
            >
              {t('Historical') || 'Histórico'}
            </Button>
          </HStack>

          {/* Selector de Dispositivo */}
          <Select
            size="xs"
            h="28px"
            w={{ base: '155px', xl: '175px' }}
            borderRadius="lg"
            bg="white"
            borderColor="gray.200"
            fontSize="11px"
            fontWeight="semibold"
            color="gray.700"
            value={activeDevice}
            onChange={handleDeviceSelect}
            aria-label={t('Filter by device') || 'Filtrar por dispositivo'}
            cursor="pointer"
            _hover={{ borderColor: 'teal.400' }}
            _focus={{ borderColor: 'teal.500', boxShadow: 'none' }}
          >
            <option value="all">{t('Dispositivo: Todos') || 'Dispositivo: Todos'}</option>
            <option value="watch">{t('Solo Smartwatch') || 'Solo Smartwatch'}</option>
            <option value="button">{t('Solo Botones SOS') || 'Solo Botones SOS'}</option>
          </Select>
        </HStack>
      )}

      {/* 2.6 Cluster de Dispositivos (Stitch Dispositivos) */}
      {isDevices && (
        <HStack spacing={2} display={{ base: 'none', lg: 'flex' }} mx={3}>
          {/* Selector de Tipo de Dispositivo */}
          <Select
            size="xs"
            h="28px"
            w={{ base: '135px', xl: '150px' }}
            borderRadius="lg"
            bg="white"
            borderColor="gray.200"
            fontSize="11px"
            fontWeight="semibold"
            color="gray.700"
            value={deviceTypeFilter}
            onChange={handleDeviceTypeChange}
            aria-label={t('Filter by device type') || 'Filtrar por tipo de dispositivo'}
            cursor="pointer"
            _hover={{ borderColor: 'teal.400' }}
            _focus={{ borderColor: 'teal.500', boxShadow: 'none' }}
          >
            <option value="all">{t('Tipo: Todos') || 'Tipo: Todos'}</option>
            <option value="smartwatch">{t('Smartwatches') || 'Smartwatches'}</option>
            <option value="emergency_button">{t('Pulsadores SOS') || 'Pulsadores SOS'}</option>
            <option value="camera">{t('Cámaras') || 'Cámaras'}</option>
          </Select>

          {/* Selector de Estado de Asignación */}
          <Select
            size="xs"
            h="28px"
            w={{ base: '125px', xl: '140px' }}
            borderRadius="lg"
            bg="white"
            borderColor="gray.200"
            fontSize="11px"
            fontWeight="semibold"
            color="gray.700"
            value={deviceAssignmentFilter}
            onChange={handleDeviceAssignmentChange}
            aria-label={t('Filter by assignment status') || 'Filtrar por estado de asignación'}
            cursor="pointer"
            _hover={{ borderColor: 'teal.400' }}
            _focus={{ borderColor: 'teal.500', boxShadow: 'none' }}
          >
            <option value="all">{t('Estado: Todos') || 'Estado: Todos'}</option>
            <option value="assigned">{t('Asignados') || 'Asignados'}</option>
            <option value="unassigned">{t('En stock') || 'En stock'}</option>
          </Select>

          {/* Selector de Centro */}
          <Select
            size="xs"
            h="28px"
            w={{ base: '135px', xl: '160px' }}
            borderRadius="lg"
            bg="white"
            borderColor="gray.200"
            fontSize="11px"
            fontWeight="semibold"
            color="gray.700"
            value={deviceCenterFilter}
            onChange={handleDeviceCenterChange}
            aria-label={t('Filter by center') || 'Filtrar por centro'}
            cursor="pointer"
            _hover={{ borderColor: 'teal.400' }}
            _focus={{ borderColor: 'teal.500', boxShadow: 'none' }}
          >
            <option value="all">{t('Centro: Todos') || 'Centro: Todos'}</option>
            {Array.isArray(allCenters) &&
              allCenters.map((c: any) => (
                <option key={String(c?.centerId || c?.id)} value={String(c?.centerId || c?.id)}>
                  {c?.name}
                </option>
              ))}
          </Select>
        </HStack>
      )}

      {/* 3. Right: Notifications Bell & User Profile Avatar (Siempre presentes) */}
      <HStack spacing={3} align="center" flexShrink={0}>
        <HeaderNotificationBell />

        <Tooltip label={`${userFullName} • ${displayRole}`} placement="bottom-end" hasArrow>
          <Avatar
            size="sm"
            name={userFullName}
            bg="teal.500"
            color="white"
            boxShadow="0 1px 3px rgba(0, 0, 0, 0.1)"
            cursor="pointer"
            border="2px solid"
            borderColor="teal.400"
            _hover={{ transform: 'scale(1.05)' }}
            transition="transform 0.15s ease"
          />
        </Tooltip>
      </HStack>
    </Flex>
  );
};

export default MobileNav;
